import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { uuidv7 } from 'uuidv7';
import { z } from 'zod';
import { useBudgetId } from '../../budget/budget-store';
import { useCategories } from '../api/use-get-categories';
import { useUpdateCategoryApi } from '../api/use-update-category';
import { useCreateCategoryApi } from '../api/use-create-delete-category';
// Category Form Schema
export const categoryFormSchema = z.object({
    name: z.string().min(1, 'Category name is required'),
    notes: z.string().optional(),
});
// Category Form Hook
export const useCategoryForm = ({ category, onSuccess }) => {
    const budgetId = useBudgetId();
    const categories = useCategories();
    const categoryCount = categories.length;
    // mutations
    const { mutate: createCategory, isPending: isCreatePending } = useCreateCategoryApi({ onSuccess });
    const { mutate: updateCategory, isPending: isUpdatePending } = useUpdateCategoryApi({ onSuccess });
    // enhance schema with name uniqueness validation and trimming
    const schema = categoryFormSchema.refine((data) => {
        const trimmedName = data.name?.trim() || '';
        const isNameTaken = categories.some((cat) => cat.name.trim().toLowerCase() === trimmedName.toLowerCase() && cat.id !== category?.id);
        return !isNameTaken;
    }, {
        message: 'A category with this name already exists',
        path: ['name'],
    });
    const defaultValues = useMemo(() => toFormData(category), [category]);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [category, form, defaultValues]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the correct create or update mutation.
     *
     * @param values - Sanitized category form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values, category, budgetId, categoryCount);
        if (category) {
            updateCategory(payload);
        }
        else {
            createCategory(payload);
        }
    }, [category, budgetId, categoryCount, createCategory, updateCategory]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isCreatePending || isUpdatePending,
    };
};
/**
 * Compute the initial values for the CategoryForm.
 *
 * If `category` is provided (edit mode), it will map its fields into the form;
 * otherwise (create mode) it returns blank-form defaults.
 *
 * @param category - Optional existing Category to edit
 * @returns A fully populated CategoryForm
 */
function toFormData(category) {
    return {
        name: category?.name ?? '',
        notes: category?.notes ?? '',
    };
}
/**
 * Build the CategoryRequest payload from form values and context.
 *
 * Injects `id`, `budgetId`, and `position` based on whether this is
 * creating a new category or updating an existing one.
 *
 * @param formValues - The values from the category form
 * @param existing - The existing Category being edited, or undefined when creating
 * @param budgetId - Current budget identifier
 * @param categoryCount - Number of existing categories (for default position)
 * @returns A fully populated CategoryRequest for create/update
 */
function toRequestData(formValues, existing, budgetId, categoryCount) {
    return {
        id: existing ? existing.id : uuidv7(),
        budgetId,
        name: formValues.name,
        notes: formValues.notes,
        position: existing?.position ?? categoryCount,
    };
}
